import { registerPlugin, Capacitor } from '@capacitor/core';

/**
 * KioskMode plugin (custom, Android-only).
 *
 * Implementação Java em
 * {@code android/app/src/main/java/io/platformbuilders/efflux/player/KioskModePlugin.java}.
 *
 * Em iOS / browser / Tizen / webOS, vira no-op silencioso — o
 * {@code Capacitor.isPluginAvailable} guard cobre tudo que não é Android.
 */
export interface KioskBackendInfo {
  name: string; // "device_owner" | "stock_android" | "knox" (futuro)
  available: boolean;
  canSilentInstall: boolean;
}

export interface SilentInstallResult {
  supported: boolean;
  backend: string;
  status?: string;
  reason?: string;
}

export interface RebootDeviceResult {
  supported: boolean;
  backend: string;
  status?: string;
  reason?: string;
}

export interface ClearDeviceOwnerResult {
  supported: boolean;
  backend: string;
  status?: string;
  reason?: string;
}

export interface WatchdogStatus {
  watchdogTripped: boolean;
  bootRecoveryFailReason?: string;
  backend: string;
}

interface KioskModePlugin {
  setLocked(opts: {
    locked: boolean;
  }): Promise<{ locked: boolean; backend?: string }>;
  getLocked(): Promise<{ locked: boolean; backend?: string }>;
  getBackendInfo(): Promise<KioskBackendInfo>;
  silentInstall(opts: { apkUri: string }): Promise<SilentInstallResult>;
  rebootDevice(): Promise<RebootDeviceResult>;
  clearDeviceOwner(): Promise<ClearDeviceOwnerResult>;
  setDeviceCredentials(opts: {
    deviceId: string;
    deviceToken: string;
    apiUrl: string;
  }): Promise<{ saved: boolean }>;
  // P6 (deep-review #15): freeze watchdog hooks
  heartbeat(): Promise<{ acknowledged: boolean; backend: string }>;
  getWatchdogStatus(): Promise<WatchdogStatus>;
  clearWatchdogStatus(): Promise<void>;
  openAndroidSettings(): Promise<{ opened: boolean }>;
  listWifiNetworks(): Promise<WifiListResult>;
  connectWifiNetwork(options: WifiConnectOptions): Promise<WifiConnectResult>;
}

/** WIFI_LIST_PLAN — uma rede Wi-Fi (conectada ou proxima). */
export interface WifiNetwork {
  ssid: string | null;
  bssid?: string | null;
  rssi: number; // dBm
  level: number; // 0..4 (barras)
  frequencyMhz?: number;
  linkSpeedMbps?: number; // so na conectada
  ip?: string | null; // so na conectada
  secured?: boolean; // so nas proximas
  capabilities?: string | null; // so nas proximas
}

/** Resultado de {@link listWifiNetworks}. */
export interface WifiListResult {
  wifiEnabled: boolean;
  permissionGranted: boolean;
  connected: WifiNetwork | null;
  networks: WifiNetwork[];
  networkCount: number;
}

/** WIFI_CONNECT_PLAN — parâmetros pra conectar a uma rede Wi-Fi. */
export interface WifiConnectOptions {
  ssid: string;
  password?: string;
  /** Rede protegida (WPA/PSK). Default: infere de `password` não-vazia. */
  secured?: boolean;
}

/** Resultado de {@link connectWifiNetwork} (a conexão real é assíncrona). */
export interface WifiConnectResult {
  requested: boolean;
  ssid: string;
  netId: number;
  enabled: boolean;
}

const Plugin = registerPlugin<KioskModePlugin>('KioskMode');

const isAvailable = (): boolean => {
  return (
    Capacitor.isNativePlatform() &&
    Capacitor.getPlatform() === 'android' &&
    Capacitor.isPluginAvailable('KioskMode')
  );
};

/**
 * Sincroniza o estado de lock do kiosk pro código nativo Android.
 *
 * No-op em outras plataformas. Erros são silenciosos — falha de bridge não
 * deve quebrar o fluxo do player.
 */
export const setNativeKioskLocked = async (locked: boolean): Promise<void> => {
  if (!isAvailable()) return;
  try {
    await Plugin.setLocked({ locked });
  } catch (e) {
    console.warn('[KioskMode] setLocked failed:', e);
  }
};

/**
 * Retorna info do backend ativo (Device Owner, Stock Android, Knox no futuro).
 * No-op em plataformas não-Android — retorna {@code null}.
 */
export const getNativeKioskBackend =
  async (): Promise<KioskBackendInfo | null> => {
    if (!isAvailable()) return null;
    try {
      return await Plugin.getBackendInfo();
    } catch (e) {
      console.warn('[KioskMode] getBackendInfo failed:', e);
      return null;
    }
  };

/**
 * Tenta instalar APK silenciosamente — só funciona se o backend ativo for
 * Device Owner ou Knox. Retorna {@code supported: false} se backend é
 * Stock Android (cai no fallback PackageInstaller no caller).
 */
export const tryNativeSilentInstall = async (
  apkUri: string,
): Promise<SilentInstallResult | null> => {
  if (!isAvailable()) return null;
  try {
    return await Plugin.silentInstall({ apkUri });
  } catch (e) {
    console.warn('[KioskMode] silentInstall failed:', e);
    return null;
  }
};

/**
 * Dispara reboot do dispositivo via DPM.reboot (Android Device Owner).
 * Retorna {supported: false} em backends que não suportam (incluindo
 * outras plataformas via no-op silencioso).
 */
export const tryNativeRebootDevice =
  async (): Promise<RebootDeviceResult | null> => {
    if (!isAvailable()) return null;
    try {
      return await Plugin.rebootDevice();
    } catch (e) {
      console.warn('[KioskMode] rebootDevice failed:', e);
      return null;
    }
  };

/**
 * Recovery: limpa o status de Device Owner do tablet — remove TODAS as policies
 * de kiosk aplicadas (Lock Task features, uninstall block, user restrictions).
 *
 * <p>Operação <b>irreversível</b> nessa sessão. Após executar com sucesso, o
 * tablet volta a ser "removível" e perde todos os privilégios elevados.
 * Reprovisionar exige factory reset + {@code dpm set-device-owner} novamente
 * via ADB.</p>
 *
 * <p>Retorna {@code supported: false} se backend ativo não é Device Owner
 * (Stock Android, browser, etc). No-op silencioso em plataformas não-Android
 * (retorna {@code null}).</p>
 */
export const tryClearDeviceOwner =
  async (): Promise<ClearDeviceOwnerResult | null> => {
    if (!isAvailable()) return null;
    try {
      return await Plugin.clearDeviceOwner();
    } catch (e) {
      console.warn('[KioskMode] clearDeviceOwner failed:', e);
      return null;
    }
  };

/**
 * Espelha as credenciais do device em SharedPreferences nativo. Necessário pra
 * o {@code ScreenOffReceiver} (BroadcastReceiver fora do WebView) conseguir
 * reportar tampering events — ele não tem acesso ao localStorage.
 *
 * <p>Chamar uma vez no init do app, e novamente sempre que o token rotacionar
 * ou a URL do backend mudar. No-op em plataformas não-Android.</p>
 */
export const setNativeDeviceCredentials = async (
  deviceId: string,
  deviceToken: string,
  apiUrl: string,
): Promise<void> => {
  if (!isAvailable()) return;
  if (!deviceId || !deviceToken || !apiUrl) return;
  try {
    await Plugin.setDeviceCredentials({ deviceId, deviceToken, apiUrl });
  } catch (e) {
    console.warn('[KioskMode] setDeviceCredentials failed:', e);
  }
};

/**
 * P6 (deep-review #15): heartbeat pro freeze watchdog do DeviceOwnerBackend.
 *
 * <p>Chamar a cada ~30s enquanto kiosk estiver ativo. Watchdog usa o
 * timestamp pra detectar se o WebView travou (sem heartbeat por >10min)
 * e auto-desativa Lock Task pra liberar o operador em campo.</p>
 *
 * <p>No-op em plataformas que não têm o plugin (browser, iOS, Tizen, webOS).</p>
 */
export const sendKioskHeartbeat = async (): Promise<void> => {
  if (!isAvailable()) return;
  try {
    await Plugin.heartbeat();
  } catch (e) {
    // Best-effort — falha de heartbeat não deve quebrar nada
    console.warn('[KioskMode] heartbeat failed:', e);
  }
};

/**
 * P6: lê estado de watchdog/boot-recovery — heartbeat client-side reporta
 * isso pro admin pra investigação remota.
 */
export const getKioskWatchdogStatus =
  async (): Promise<WatchdogStatus | null> => {
    if (!isAvailable()) return null;
    try {
      return await Plugin.getWatchdogStatus();
    } catch (e) {
      console.warn('[KioskMode] getWatchdogStatus failed:', e);
      return null;
    }
  };

/** P6: limpa flag de watchdog tripped após admin acknowledgment. */
export const clearKioskWatchdogStatus = async (): Promise<void> => {
  if (!isAvailable()) return;
  try {
    await Plugin.clearWatchdogStatus();
  } catch (e) {
    console.warn('[KioskMode] clearWatchdogStatus failed:', e);
  }
};

/**
 * v1.0.142 — abre as Configurações do Android via Intent nativo. Saída
 * determinística pra o operador (após desativar o kiosk) chegar em USB debug /
 * Restaurar fábrica sem depender de nav bar / Home / launcher. No-op (retorna
 * false) fora de Android nativo.
 */
export const openAndroidSettings = async (): Promise<boolean> => {
  if (!isAvailable()) return false;
  try {
    const res = await Plugin.openAndroidSettings();
    return res?.opened ?? true;
  } catch (e) {
    console.warn('[KioskMode] openAndroidSettings failed:', e);
    return false;
  }
};

/**
 * WIFI_LIST_PLAN — lista a rede conectada + redes proximas (scan).
 * No-op fora de Android nativo (retorna null). Acionado on-demand via
 * RemoteOps (wifi:list). Erros viram null — o caller trata como "indisponivel".
 */
export const listWifiNetworks = async (): Promise<WifiListResult | null> => {
  if (!isAvailable()) return null;
  try {
    return await Plugin.listWifiNetworks();
  } catch (e) {
    console.warn('[KioskMode] listWifiNetworks failed:', e);
    return null;
  }
};

/**
 * WIFI_CONNECT_PLAN — conecta a uma rede Wi-Fi escolhida no admin.
 * No-op fora de Android nativo (retorna null). Acionado on-demand via
 * RemoteOps (wifi:connect). ⚠️ Ao trocar de rede o tablet derruba a conexão
 * atual — normal; ele reassocia e volta sozinho.
 */
export const connectWifiNetwork = async (
  options: WifiConnectOptions,
): Promise<WifiConnectResult | null> => {
  if (!isAvailable()) return null;
  try {
    return await Plugin.connectWifiNetwork(options);
  } catch (e) {
    console.warn('[KioskMode] connectWifiNetwork failed:', e);
    return null;
  }
};

export default {
  setLocked: setNativeKioskLocked,
  getBackend: getNativeKioskBackend,
  silentInstall: tryNativeSilentInstall,
  rebootDevice: tryNativeRebootDevice,
  clearDeviceOwner: tryClearDeviceOwner,
  setDeviceCredentials: setNativeDeviceCredentials,
  heartbeat: sendKioskHeartbeat,
  getWatchdogStatus: getKioskWatchdogStatus,
  clearWatchdogStatus: clearKioskWatchdogStatus,
  openAndroidSettings,
  listWifiNetworks,
  connectWifiNetwork,
};
